import type { CSSProperties, ReactNode } from 'react';
import { StatusDot } from '@paige/ui';
import { useFormat } from '@paige/format';
import type {
  AdminGpuCards,
  GpuCloudPool,
  GpuDesktop,
  GpuDesktopModel,
  GpuModelSplit,
} from './gpu-types';
import {
  cardName,
  cloudWord,
  deskShare,
  desktopWord,
  gib,
  modelName,
  modelTone,
  splitsOf,
} from './gpu-words';
import './gpu.css';

/** The card's memory as one bar: a segment per model placed on it. Never the driver's free memory (unreliable under WSL). */
function MemoryBar({ d }: { d: GpuDesktop }): ReactNode {
  const share = (mib: number) =>
    `${Math.max(0, Math.min(100, (mib / Math.max(1, d.totalMib)) * 100)).toFixed(1)}%`;
  const held = d.models.reduce((n, m) => n + m.heldMib, 0);
  return (
    <div
      className="gpu-mem"
      role="img"
      aria-label={`${gib(held)} of ${gib(d.totalMib)} GiB placed for Cortexa`}
    >
      {d.models.map((m) => (
        <span
          key={m.model}
          className={`gpu-mem__seg gpu-tone--${modelTone[m.model]}`}
          style={{ '--gpu-share': share(m.heldMib) } as CSSProperties}
        />
      ))}
    </div>
  );
}

/** One model on a desktop: its name, shape, and what it is doing. */
function ModelChip({ m }: { m: GpuDesktopModel }): ReactNode {
  const f = useFormat();
  const shape =
    m.model === 'reason' && m.lanes > 0 ? `, ${m.lanes} ${m.lanes === 1 ? 'lane' : 'lanes'}` : '';
  return (
    <li className={`gpu-model gpu-tone--${modelTone[m.model]}`}>
      <span className="gpu-model__name">
        {modelName[m.model]}
        <span className="gpu-model__shape">
          {gib(m.heldMib)} GiB{shape}
          {m.state === 'warm' ? '' : `, ${m.state}`}
        </span>
      </span>
      <span className="gpu-model__calls">
        <strong>{f.int(m.inFlight)}</strong> now · <strong>{f.int(m.served)}</strong> in 5 min
      </span>
    </li>
  );
}

function Desktop({ d }: { d: GpuDesktop }): ReactNode {
  const word = desktopWord(d);
  const tone = d.state === 'paused' ? 'warning' : word === 'Working' ? 'success' : 'neutral';
  return (
    <article className="gpu-tile" data-machine={d.machine}>
      <header className="gpu-tile__head">
        <strong className="gpu-tile__name">{d.machine}</strong>
        <span className="gpu-tile__card">{cardName(d.card)}</span>
      </header>
      <p className="gpu-tile__word">
        <StatusDot tone={tone} size="sm" label={word} /> {word}
        <span className="gpu-tile__busy">{d.busyPercent}% busy</span>
      </p>
      <MemoryBar d={d} />
      {d.models.length > 0 ? (
        <ul className="gpu-tile__models">
          {d.models.map((m) => (
            <ModelChip key={m.model} m={m} />
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function Cloud({ p }: { p: GpuCloudPool }): ReactNode {
  const f = useFormat();
  const on = p.state !== 'off';
  return (
    <article
      className={on ? 'gpu-tile gpu-tile--cloud gpu-tile--on' : 'gpu-tile gpu-tile--cloud'}
      data-pool={p.id}
    >
      <header className="gpu-tile__head">
        <strong className="gpu-tile__name">{modelName[p.model]}</strong>
        <span className="gpu-tile__card">cloud card</span>
      </header>
      <p className="gpu-tile__word">
        <StatusDot tone={on ? 'warning' : 'neutral'} size="sm" label={cloudWord(p)} />{' '}
        {cloudWord(p)}
      </p>
      <p className="gpu-tile__served">
        <strong>{f.int(p.served)}</strong> calls in 5 min
      </p>
    </article>
  );
}

/** A model's last five minutes as one bar, desk against cloud, and the words for it. */
function Split({ m }: { m: GpuModelSplit }): ReactNode {
  const f = useFormat();
  const desk = deskShare(m);
  const total = m.desk + m.cloud + m.unserved;
  const copies =
    m.desktopCopies === 0
      ? 'no desktop copy'
      : `${m.desktopCopies} desktop ${m.desktopCopies === 1 ? 'copy' : 'copies'}`;
  return (
    <li className="gpu-split">
      <span className={`gpu-split__name gpu-tone--${modelTone[m.model]}`}>
        {modelName[m.model]}
      </span>
      <span className="gpu-split__bar" aria-hidden="true">
        <span
          className="gpu-split__desk"
          style={{ '--gpu-share': `${((desk ?? 0) * 100).toFixed(1)}%` } as CSSProperties}
        />
      </span>
      <span className="gpu-split__words">
        {desk === null
          ? `no calls in 5 min, ${copies}`
          : `${f.percent(Math.round(desk * 100))} on desks of ${f.int(total)}, ${copies}`}
        {m.waiting > 0 ? `, ${f.int(m.waiting)} waiting` : ''}
      </span>
    </li>
  );
}

/**
 * The GPU cards under Stages and queues: each desktop card connected to the relay with what placement put on it and
 * the calls it answered, each cloud pool behind the relay, and each model's desk and cloud split over the last five
 * minutes. From `GET /v1/admin/gpu-cards`; nothing on it is sample data.
 */
export function GpuCardsPanel({
  cards,
  stale = false,
  more,
}: {
  cards: AdminGpuCards;
  stale?: boolean;
  /** A link to every desktop of the last 30 days (Desktop GPUs). */
  more?: ReactNode;
}): ReactNode {
  return (
    <section className={stale ? 'gpu-panel gpu-panel--stale' : 'gpu-panel'} aria-label="GPU cards">
      <div className="gpu-panel__title">
        <span>GPU cards</span>
        <span className="gpu-panel__note">
          which card does what, last {cards.windowMinutes} minutes
        </span>
        {more ? <span className="gpu-panel__more">{more}</span> : null}
      </div>
      <ul className="gpu-panel__splits">
        {splitsOf(cards.models).map((m) => (
          <Split key={m.model} m={m} />
        ))}
      </ul>
      <div className="gpu-panel__row">
        {!cards.relayAnswering ? (
          <p className="gpu-panel__quiet" role="status">
            The relay did not answer, so the desktops cannot be shown.
          </p>
        ) : cards.desktops.length === 0 ? (
          <p className="gpu-panel__quiet">No desktop card is connected.</p>
        ) : (
          cards.desktops.map((d) => <Desktop key={d.machine} d={d} />)
        )}
        {cards.cloudPools.map((p) => (
          <Cloud key={p.id} p={p} />
        ))}
      </div>
    </section>
  );
}
